import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { RequestsNewService } from '../services/RequestsNew.service';
import { AIService } from '@/core/ai/ai.service';

export function useRequestsNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    zoneId: '',
    requestType: 'thi_cong',
    priority: 'normal',
    isUrgent: false,
    reason: '',
    impactScope: 'toan_bo',
    relatedAssetId: '',
    relatedUserId: '',
    deadlineDate: '',
    expectedDate: '',
    group: '',
    category: '',
    items: [] as any[],
  });

  const [isChatOpen, setIsChatOpen] = useState(false);

  const applyAIData = (data: any) => {
    setFormData(prev => ({
      ...prev,
      title: data.title,
      description: data.description,
      priority: data.priority,
      items: data.items,
    }));
    setIsChatOpen(false);
  };

  const projectsQuery = useQuery({
    queryKey: ['projects', 'simple-list', companyId],
    queryFn: () => RequestsNewService.getProjects(companyId),
    enabled: !!companyId,
  });

  const zonesQuery = useQuery({
    queryKey: ['zones', formData.projectId],
    queryFn: () => RequestsNewService.getZones(formData.projectId),
    enabled: !!formData.projectId,
  });

  const proposalGroupsQuery = useQuery({
    queryKey: ['proposalGroups', companyId],
    queryFn: () => RequestsNewService.getProposalGroups(companyId),
    enabled: !!companyId,
  });

  const approversQuery = useQuery({
    queryKey: ['approvers', companyId],
    queryFn: () => RequestsNewService.getApprovers(companyId),
    enabled: !!companyId,
  });

  const selectedGroup = proposalGroupsQuery.data?.find((g: any) => g.code === formData.group);
  
  const proposalCategoriesQuery = useQuery({
    queryKey: ['proposalCategories', companyId, selectedGroup?.id],
    queryFn: () => RequestsNewService.getProposalCategories(companyId, selectedGroup?.id || ''),
    enabled: !!companyId && !!selectedGroup?.id,
  });

  const proposalPurposesQuery = useQuery({
    queryKey: ['proposalPurposes', companyId],
    queryFn: () => RequestsNewService.getProposalPurposes(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => {
      const payload = {
        companyId,
        projectId: data.projectId,
        zoneId: data.zoneId,
        title: data.title,
        description: data.description,
        isUrgent: data.isUrgent,
        requestType: data.requestType,
        customData: {
          reason: data.reason,
          impactScope: data.impactScope,
          relatedAssetId: data.relatedAssetId,
          relatedUserId: data.relatedUserId,
          deadlineDate: data.deadlineDate,
          expectedDate: data.expectedDate,
          group: data.group,
          category: data.category,
          purpose: data.purpose,
          priority: data.priority,
        },
        items: data.items
      };
      return RequestsNewService.createRequest(userId, payload);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      toast.success('Tạo đề xuất thành công');
      // Tạm thời redirect về danh sách do chưa có UI Step 2
      navigate(`/requests`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Không thể tạo đề xuất');
    }
  });

  const analyzeWithAI = async (file: File | string) => {
    try {
      const result = await AIService.analyzeRequest(file, companyId);
      setFormData(prev => ({
        ...prev,
        title: result.title,
        description: result.description,
        priority: result.priority,
        items: result.suggestedItems,
      }));
      toast.success('AI đã hoàn tất phân tích và điền thông tin gợi ý');
    } catch (error) {
      toast.error('AI không thể phân tích hình ảnh vào lúc này');
    }
  };

  return {
    formData,
    projects: projectsQuery.data || [],
    zones: zonesQuery.data || [],
    proposalGroups: proposalGroupsQuery.data || [],
    proposalCategories: proposalCategoriesQuery.data || [],
    proposalPurposes: proposalPurposesQuery.data || [],
    approvers: approversQuery.data || [],
    isLoading: projectsQuery.isLoading || zonesQuery.isLoading || proposalGroupsQuery.isLoading || approversQuery.isLoading || proposalCategoriesQuery.isLoading || proposalPurposesQuery.isLoading,
    isSaving: createMutation.isPending,
    isChatOpen,
    setIsChatOpen,
    setFormData,
    createRequest: () => createMutation.mutate(formData),
    applyAIData,
  };
}
